/* eslint-disable array-callback-return */
import React, { PureComponent } from 'react';
import * as dates from '@simplybenefits/sb-dates';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { MessageCard, InvoiceTile, Button, CurrentTransaction, Container, Content, Title, Theme, i18n, tokens, localizationMaps } from '@simplybenefits/sb-ui-library';
import { setCurrentSidebarItem } from '../../actions/user.js';
import { getInvoice } from '../../actions/invoices.js';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import { downloadPDFToBrowser } from '../../services/helpers.js';

class Invoices extends PureComponent {
    state = {
        invoice: null
    };

    componentDidMount() {
        this.props.dispatch(setCurrentSidebarItem('invoices'));
    }

    _selectInvoice = async (selectedInvoice) => {
        const invoice = await this.props.dispatch(getInvoice(selectedInvoice.invoiceNum));

        this.setState({
            invoice: {
                ...invoice,
                meta: {
                    ...selectedInvoice,
                    ...invoice.meta
                }
            }
        });
    };

    _showHistory = () => {
        this.setState({
            invoice: null
        });
    };

    /**
     *
     * @param {*} number
     */
    formatNumber = (number) => `$${ (number || 0).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') }`;

    /**
     *
     * @param {*} benefit
     * @param {*} transaction
     * @returns
     */
    _determineWhetherToShow = (benefit) => {
        if (benefit === "wello" && typeof this.state.invoice.amounts[benefit]?.premium !== "number") {
            return null;
        }

        if (benefit === "marketplace" && this.state.invoice.amounts.voluntaryBenefits && this.state.invoice.amounts.voluntaryBenefits.premium !== 0) {
            return null;
        }

        if (benefit === "voluntaryBenefits" && this.state.invoice.amounts.marketplace && this.state.invoice.amounts.marketplace.premium !== 0) {
            return null;
        }

        return (
            (benefit !== "totals") && 
            (this.state.invoice.amounts[benefit]?.premium > 0 || (this.state.invoice.amounts[benefit]?.adjustments instanceof Object ?
                this.state.invoice.amounts[benefit]?.adjustments.total
                :
                this.state.invoice.amounts[benefit]?.adjustments !== 0))
        );
    };

    /**
     *
     * @param {*} x
     */
    _numberWithCommas = (x) => x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    _downloadCSV = (invoiceNum) => {
        let items = [];

        const benefits = {
            'life': {
                title: i18n.t(tokens.titles.lifeInsurance),
                volumeType: 'number'
            },
            'add': {
                title: i18n.t(tokens.titles["ad&d"]),
                volumeType: 'number'
            },
            'ltd': {
                title: i18n.t(tokens.titles.ltd),
                volumeType: 'number'
            },
            'std': {
                title: i18n.t(tokens.titles.std),
                volumeType: 'number'
            },
            'depLife': {
                title: i18n.t(tokens.titles.depLife),
                volumeType: 'text'
            },
            'ci': {
                title: i18n.t(tokens.titles.ci),
                volumeType: 'text'
            },
            'ciSpouse': {
                title: i18n.t(tokens.titles.ciSpouse),
                volumeType: 'text'
            },
            'ciChild': {
                title: i18n.t(tokens.titles.ciChild),
                volumeType: 'text'
            },
            'health': {
                title: i18n.t(tokens.titles.health),
                volumeType: 'text'
            },
            'outOfCountry': {
                title: i18n.t(tokens.titles.outOfCountry),
                volumeType: 'text'
            },
            'stopLoss': {
                title: i18n.t(tokens.titles.stopLoss),
                volumeType: 'text'
            },
            'hsa': {
                title: i18n.t(tokens.titles.hsa),
                volumeType: 'text'
            },
            'lsa': {
                title: i18n.t(tokens.titles.lsa),
                volumeType: 'text'
            },
            'costPlus': {
                title: i18n.t(tokens.titles.costPlus),
                volumeType: 'text'
            },
            'wello': {
                title: 'Wello',
                volumeType: 'text'
            },
            'virtualHealthcare': {
                title: i18n.t(tokens.labels.invoices.virtualHealthcare),
                volumeType: 'text'
            },
            'financialAwareness': {
                title: i18n.t(tokens.labels.invoices.financialAwareness),
                volumeType: 'text'
            },
            'efap': {
                title: i18n.t(tokens.titles.efap),
                volumeType: 'text'
            },
            'dental': {
                title: i18n.t(tokens.titles.dental),
                volumeType: 'text'
            },
            'marketplace': {
                title: i18n.t(tokens.titles.marketplace),
                volumeType: 'dash'
            },
            'voluntaryBenefits': {
                title: i18n.t(tokens.titles.voluntaryBenefits),
                volumeType: 'dash'
            },
            'optionalBenefits': {
                title: i18n.t(tokens.titles.optionalBenefits),
                volumeType: 'dash'
            },
            'miscPremiums': {
                title: i18n.t(tokens.titles.misc),
                volumeType: 'dash'
            },
            'totals': {
                title: i18n.t(tokens.labels.total),
                volumeType: 'number'
            }
        };
        
        // eslint-disable-next-line no-unused-vars
        Object.keys(benefits).map((benefit, i) => {
            const benefitParams = benefits[benefit];
            let volume = '-';
            let rate = 0;
            let premium = 0;
            let adj = 0;
            let HST = 0;
            let GST = 0;
            let PST = 0;
            let RST = 0;

            if (this.state.invoice.amounts[benefit]) {
                // this first block is to support very old legacy invoices
                if (((benefit === 'marketplace') || benefit === 'miscPremiums') && this.state.invoice.amounts[benefit].totals) {
                    premium = this.state.invoice.amounts[benefit].totals.premium;
                    HST = this.state.invoice.amounts[benefit].totals.HST;
                    GST = this.state.invoice.amounts[benefit].totals.GST;
                    PST = this.state.invoice.amounts[benefit].totals.PST;
                    RST = this.state.invoice.amounts[benefit].totals.RST || 0;
                    adj = this.state.invoice.amounts[benefit].totals.adjustments;
                    rate = this.state.invoice.amounts[benefit].rate;
                } else {
                    premium = this.state.invoice.amounts[benefit].premium;
                    HST = this.state.invoice.amounts[benefit].HST;
                    GST = this.state.invoice.amounts[benefit].GST;
                    PST = this.state.invoice.amounts[benefit].PST;
                    RST = this.state.invoice.amounts[benefit].RST || 0;
                    rate = this.state.invoice.amounts[benefit].rate;

                    if (this.state.invoice.amounts[benefit].adjustments instanceof Object) {
                        adj = this.state.invoice.amounts[benefit].adjustments.net;
                        // need to add the adjustment taxes to the overall benefit taxes
                        HST += this.state.invoice.amounts[benefit].adjustments.HST;
                        GST += this.state.invoice.amounts[benefit].adjustments.GST;
                        PST += this.state.invoice.amounts[benefit].adjustments.PST;
                        RST += this.state.invoice.amounts[benefit].adjustments.RST || 0;
                    } else {
                        adj = this.state.invoice.amounts[benefit].adjustments;
                    }

                    if (benefitParams.volumeType === 'number') {
                        volume = this._numberWithCommas(Math.ceil(this.state.invoice.amounts[benefit].volume));
                    } else if (benefitParams.volumeType === 'text') {
                        volume = localizationMaps.fromEnglish('coverageTypes', this.state.invoice.amounts[benefit].coverageType);
                    }
                }
            }

            if (this._determineWhetherToShow(benefit)) {
                items.push({ 
                    "benefit": benefitParams.title === `Wello` ? "Maple" : benefitParams.title,
                    "volume": volume,
                    "rate": this.formatNumber(rate),
                    "premium": this.formatNumber(premium),
                    "adj": this.formatNumber(adj),
                    "gst/hst": this.formatNumber(GST + HST),
                    "pst": this.formatNumber(PST + RST),
                    "totals": this.formatNumber(premium + GST + HST + PST + RST + adj)
                });
            }
        });

        items.push({
            "benefit": i18n.t(tokens.labels.invoices.carryForward),
            "volume": "-",
            "rate": "-",
            "premium": "-",
            "adj": "-",
            "gst/hst": "-",
            "pst": "-",
            "totals": this.formatNumber(this.state.invoice.amounts.totals.carryForward)
        });

        if (this.state.invoice.amounts.totals.CCFee) {
            items.push({
                "benefit": i18n.t(tokens.labels.invoices.convenienceFee),
                "volume": "-",
                "rate": "-",
                "premium": "-",
                "adj": "-",
                "gst/hst": "-",
                "pst": "-",
                "totals": this.formatNumber(this.state.invoice.amounts.totals.CCFee)
            });
        }

        items.push({
            "benefit": i18n.t(tokens.labels.total),
            "volume": "-",
            "rate": "-",
            "premium": this.formatNumber(this.state.invoice.amounts.totals.premium),
            "adj": this.state.invoice.amounts.totals.adjustments instanceof Object ?
                this.formatNumber(this.state.invoice.amounts.totals.adjustments.net) :
                this.formatNumber(this.state.invoice.amounts.totals.adjustments),
            "gst/hst": this.formatNumber(this.state.invoice.amounts.totals.HST + this.state.invoice.amounts.totals.GST),
            "pst": this.formatNumber(this.state.invoice.amounts.totals.PST + (this.state.invoice.amounts.totals.RST || 0)),
            "totals": this.formatNumber(this.state.invoice.amounts.totals.grandTotal + this.state.invoice.amounts.totals.carryForward)
        });

        let title = `${ i18n.t(tokens.titles.invoice) }-${ invoiceNum }`;

        if (this.props.dateRange) {
            title += `(${ this.props.dateRange.startDate } - ${ this.props.dateRange.endDate })`;
        }

        const options = {
            title,
            fieldSeparator: ',',
            filename: `${ invoiceNum }-${ new Date().toLocaleDateString() }`,
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: true,
            useTextFile: false,
            useBom: true,
            headers: [ 
                i18n.t(tokens.labels.invoices.benefitService),
                i18n.t(tokens.labels.invoices.volume),
                i18n.t(tokens.labels.invoices.rate),
                i18n.t(tokens.labels.invoices.premium),
                i18n.t(tokens.labels.invoices.adj),
                i18n.t(tokens.labels.invoices.gstHst),
                i18n.t(tokens.labels.invoices.pst),
                i18n.t(tokens.labels.total)
            ]
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(items);
    };

    
    render() {

        const styles = {
            contentBlock: { 
                backgroundColor: Theme.colors.surface,
                boxShadow: '0 1px 2px rgba(0, 0, 0, .15)',
                display: 'block',
                margin: 'auto auto 30px auto',
                padding: '30px',
                position: 'relative',
                zIndex: 3
            },
            invoiceHistoryContainer: {
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'row'
            },
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: "space-between"
            }
        };

        if (this.state.invoice) {
            return (
                <Container>
                    <Content>
                        <div style={ styles.titleBlock }>
                            <Title title={ i18n.t(tokens.titles.invoiceDate, { date: dates.getLocalDisplay(this.state.invoice.meta.dateAdded, 'MMMM YYYY', i18n.locale) }) } customStyle={{ flex: 1 }} />

                            <Button
                                id="view-history"
                                color="primary"
                                customStyle={{
                                    height: 50,
                                    width: 180,
                                    textTransform: "capitalize",
                                    fontSize: 16
                                }}
                                onClick={ this._showHistory }
                            >
                                { i18n.t(tokens.labels.invoices.viewHistory) }
                            </Button>
                        </div>

                        <div style={ styles.contentBlock }>

                            <Button
                                id="view-history"
                                transparent
                                customStyle={{
                                    height: 50,
                                    textTransform: "capitalize",
                                    fontSize: 16,
                                    paddingLeft: "10px",
                                    paddingRight: "10px",
                                    mrginRight: "10px"
                                }}
                                onClick={ () => downloadPDFToBrowser("MemberInvoice", `Invoice-${ this.state.invoice.invoiceNum }`, this.props.dispatch, { invoiceNum: this.state.invoice.invoiceNum }) }
                            >
                                { i18n.t(tokens.buttons["download invoice pdf"]) }
                            </Button>

                            <Button
                                id="view-history"
                                transparent
                                customStyle={{
                                    height: 50,
                                    textTransform: "capitalize",
                                    fontSize: 16,
                                    paddingLeft: "10px",
                                    paddingRight: "10px"
                                }}
                                onClick={ () => this._downloadCSV(this.state.invoice.invoiceNum) }
                            >
                                { i18n.t(tokens.buttons["download invoice csv"]) }
                            </Button>

                            <CurrentTransaction transaction={ this.state.invoice } isMemberInvoice={ true } />
                        </div>
                    </Content>
                </Container>
            );
        }

        return (
            <Container>
                <Content>
                    <Title title={ i18n.t(tokens.titles["invoice history"])} customStyle={{ flex: 1 }} />

                    {
                        this.props.invoices.length === 0 ?
                            <MessageCard
                                title={ i18n.t(tokens.titles["no invoice history"]) }
                                subtitle={ i18n.t(tokens.labels.invoices.youHaveNotBeenCharged) }
                            />
                            :
                            <div style={ styles.invoiceHistoryContainer }>
                                {
                                    this.props.invoices.map((invoice, i) => (
                                        <InvoiceTile
                                            testid={i === 0 ? 'invoice-history-tile' : undefined}
                                            key={invoice.dateAdded}
                                            date={invoice.dateAdded}
                                            total={ invoice.grandTotal }
                                            status={ invoice.status }
                                            isFirstInvoice={ i === (this.props.invoices.length - 1) }
                                            onClick={() => this._selectInvoice(invoice)}
                                        />
                                    ))
                                }
                            </div>
                    }
                </Content>

                <span id="claims" />
            </Container>
        );
    }
}

Invoices.propTypes = {
    invoices: PropTypes.array
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    invoices: state.invoices.items
});

export default connect(mapStateToProps)(Invoices);